import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import Login from './pages/Login';
import PasswordResetModal from './components/PasswordResetModal';

interface User {
  id: number;
  email: string;
  name: string;
  mustResetPassword: boolean;
}

const AppRouter: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already logged in or has SSO token
  useEffect(() => {
    // Check for SSO token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const ssoToken = urlParams.get('sso_token');

    if (ssoToken) {
      try {
        // Decode JWT to get user info (simple base64 decode of payload)
        const payloadBase64 = ssoToken.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));

        // Store token and user info
        localStorage.setItem('authToken', ssoToken);
        const ssoUser = {
          id: payload.id,
          email: payload.email,
          name: payload.email.split('@')[0], // Use email prefix as name if not provided
          roleId: payload.roleId,
          role: payload.role,
          mustResetPassword: false
        };
        localStorage.setItem('user', JSON.stringify(ssoUser));

        // Clean up URL by removing token parameter
        urlParams.delete('sso_token');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);

        // Set authenticated state
        setUser(ssoUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('Error processing SSO token:', error);
        // Continue to check localStorage
      }
    }

    // Check localStorage for existing session
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);

        // Show password reset modal if user needs to reset password
        if (parsedUser.mustResetPassword) {
          setShowPasswordReset(true);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }

    setIsLoading(false);
  }, []);

  const handleLoginSuccess = (loggedInUser: User, token: string) => {
    setUser(loggedInUser);
    setIsAuthenticated(true);

    // Show password reset modal if user needs to reset password
    if (loggedInUser.mustResetPassword) {
      setShowPasswordReset(true);
    }
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);

    // Update user in localStorage to clear mustResetPassword flag
    if (user) {
      const updatedUser = { ...user, mustResetPassword: false };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setShowPasswordReset(false);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <BrowserRouter>
      <App />
      {showPasswordReset && user && (
        <PasswordResetModal
          userId={user.id}
          isFirstLogin={true}
          onClose={() => {}} // Can't close if it's first login
          onSuccess={handlePasswordResetSuccess}
        />
      )}
    </BrowserRouter>
  );
};

export default AppRouter;
